import { Link } from 'react-router-dom'
import { Smartphone, Monitor, ExternalLink, PlayCircle, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { usePwaInstallStatus } from '@/lib/usePwaInstallStatus'

const APP_URL = 'https://app.binyumatv.com/?source=download'

export function Download() {
  const isInstalled = usePwaInstallStatus()

  if (isInstalled) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="max-w-xl text-center space-y-6">
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#FFD700] bg-clip-text text-transparent">
            Binyuma TV is already installed
          </h1>
          <p className="text-base md:text-lg text-gray-300">
            You&apos;ve added the Binyuma TV app to your device. Open it from your home screen or app launcher to keep watching.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/">
              <Button
                size="lg"
                className="bg-[#D72638] hover:bg-[#D72638]/90 text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-[0_0_30px_rgba(215,38,56,0.5)]"
              >
                Go to homepage
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-[#050309] to-black text-white">
      <div className="relative max-w-5xl mx-auto px-6 py-12">
        <div className="mb-8 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors"
          >
            &larr; Back to Home
          </Link>
          <a
            href={APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
          >
            <span className="inline-flex items-center gap-2 text-xs md:text-sm text-gray-400">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              Install
            </span>
          </a>
        </div>

        {/* Hero */}
        <section className="grid md:grid-cols-[1.1fr_minmax(0,0.9fr)] gap-10 md:gap-14 items-center mb-16">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1 text-xs font-medium text-[#FFD700] mb-4">
              <PlayCircle className="w-4 h-4" />
              New episodes weekly. Built for vertical drama.
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
              <span className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] bg-clip-text text-transparent">
                Watch African stories
              </span>
              <br />
              <span className="text-white">like never before.</span>
            </h1>
            <p className="text-base md:text-lg text-gray-300 mb-8 max-w-xl">
              Binyuma TV is a vertical-first streaming experience for premium African drama series.
              Install the app to swipe through episodes, get personalised recommendations, and watch anywhere.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
              <a
                href={APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button
                  variant="primary"
                  className="w-full sm:w-auto bg-[#D72638] hover:bg-[#D72638]/90 text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-[0_0_40px_rgba(215,38,56,0.65)]"
                >
                  Start watching
                  <ExternalLink className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
            <p className="text-xs md:text-sm text-gray-400 max-w-xs mt-3">
              You&apos;ll see an install prompt. Tap{' '}
              <span className="font-semibold">Add to Home Screen</span> to add a shortcut to your home screen.
            </p>
          </div>

          {/* Device preview with live app iframe */}
          <div className="relative max-w-[320px] mx-auto md:mx-0 md:ml-auto">
            {/* Animated spectrum glow around phone */}
            <div
              className="pointer-events-none absolute -inset-8 rounded-[40px] blur-3xl opacity-60 animate-[phoneGlowSpin_8s_linear_infinite]"
              style={{
                background:
                  'conic-gradient(from 0deg, #3b82f6, #8b5cf6, #06b6d4, #22c55e, #eab308, #f97316, #ef4444, #ec4899, #8b5cf6, #3b82f6)',
              }}
            />
            <div className="relative z-10 rounded-[32px] border border-white/10 bg-gradient-to-b from-[#1b1026] via-[#050309] to-black p-[10px] shadow-[0_20px_60px_rgba(0,0,0,0.7)]">
              <div className="rounded-[26px] bg-black overflow-hidden aspect-[9/19] w-full">
                <iframe
                  src={APP_URL}
                  title="Binyuma TV app"
                  className="w-full h-full border-0 block rounded-[26px]"
                  allow="fullscreen"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </section>

        {/* Feature highlights */}
        <section className="mb-16">
          <h2 className="text-xl md:text-2xl font-semibold mb-6">
            Why install the Binyuma TV app?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black p-5">
              <h3 className="font-semibold text-white mb-2 text-sm md:text-base">
                Built for vertical drama
              </h3>
              <p className="text-xs md:text-sm text-gray-400">
                Full-screen vertical stories that feel native to your phone, not a TV screen
                squeezed to fit.
              </p>
            </div>
            <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black p-5">
              <h3 className="font-semibold text-white mb-2 text-sm md:text-base">
                Always one tap away
              </h3>
              <p className="text-xs md:text-sm text-gray-400">
                Add Binyuma TV to your home screen so you can jump back into your shows instantly,
                without hunting for tabs.
              </p>
            </div>
            <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black p-5">
              <h3 className="font-semibold text-white mb-2 text-sm md:text-base">
                Light, fast, no app store
              </h3>
              <p className="text-xs md:text-sm text-gray-400">
                No big downloads or updates. The PWA stays up to date automatically whenever you
                open it.
              </p>
            </div>
          </div>
        </section>

        {/* Install instructions */}
        <section className="mb-16">
          <h2 className="text-xl md:text-2xl font-semibold mb-6">How to install</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-5 rounded-xl bg-gradient-to-br from-gray-900 to-black border border-gray-800">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-semibold text-white text-sm md:text-base">iOS (Safari)</h3>
              </div>
              <ul className="space-y-1 text-xs md:text-sm text-gray-400 list-disc list-inside">
                <li>Tap &ldquo;Install web app&rdquo; above to open the app.</li>
                <li>Tap the Share icon in Safari.</li>
                <li>Choose &ldquo;Add to Home Screen&rdquo; and confirm.</li>
              </ul>
            </div>

            <div className="p-5 rounded-xl bg-gradient-to-br from-gray-900 to-black border border-gray-800">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-semibold text-white text-sm md:text-base">Android (Chrome)</h3>
              </div>
              <ul className="space-y-1 text-xs md:text-sm text-gray-400 list-disc list-inside">
                <li>Tap &ldquo;Install web app&rdquo; above to open the app.</li>
                <li>Accept the install prompt when it appears,</li>
                <li>Or tap the ⋮ menu and choose &ldquo;Install app&rdquo;.</li>
              </ul>
            </div>

            <div className="p-5 rounded-xl bg-gradient-to-br from-gray-900 to-black border border-gray-800">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 flex items-center justify-center">
                  <Monitor className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-semibold text-white text-sm md:text-base">
                  Desktop (Chrome / Edge)
                </h3>
              </div>
              <ul className="space-y-1 text-xs md:text-sm text-gray-400 list-disc list-inside">
                <li>Tap &ldquo;Install web app&rdquo; above to open app.binyumatv.com.</li>
                <li>Click the install icon in the address bar,</li>
                <li>Or open the browser menu and choose &ldquo;Install Binyuma TV&rdquo;.</li>
              </ul>
            </div>
          </div>
        </section>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/10 pt-6">
          <p className="text-xs md:text-sm text-gray-500">
            Ready to watch? Install the app or{' '}
            <a
              href={APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#D4AF37] hover:underline"
            >
              open in your browser
            </a>
            .
          </p>
          <Link to="/">
            <Button
              variant="secondary"
              className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
            >
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  )
}

